<template>
  <div class="conv-page">
    <div class="header">
      <h2>转化分析 <span class="tz-hint">时间：北京时间 (Asia/Shanghai)</span></h2>
      <div class="filters">
        <el-radio-group v-model="days" size="small" @change="reload">
          <el-radio-button :label="1">近1天</el-radio-button>
          <el-radio-button :label="7">近7天</el-radio-button>
          <el-radio-button :label="30">近30天</el-radio-button>
        </el-radio-group>
        <el-button type="primary" plain size="small" :loading="loading" @click="reload">刷新</el-button>
      </div>
    </div>

    <el-row :gutter="16" class="stats-row">
      <el-col :span="6">
        <div class="stat-card">
          <div class="stat-label">事件总数</div>
          <div class="stat-value">{{ summary.total_events || 0 }}</div>
        </div>
      </el-col>
      <el-col :span="6" v-for="key in highlightKeys" :key="key">
        <div class="stat-card">
          <div class="stat-label">{{ labelOf(key) }}</div>
          <div class="stat-value">{{ countOf(key) }}</div>
        </div>
      </el-col>
    </el-row>

    <!-- 顶部 Tab：确保「访客访问 / 登录」「IP 个人画像」在首屏可见 -->
    <el-tabs v-model="activeTab" class="conv-tabs" type="card">
      <el-tab-pane label="转化概览" name="overview">
        <el-row :gutter="16">
          <el-col :span="14">
            <div class="panel">
              <h3>转化漏斗</h3>
              <div v-loading="loading" class="funnel">
                <div v-for="(step, idx) in funnel" :key="step.event_type" class="funnel-row">
                  <div class="funnel-meta">
                    <span class="idx">{{ idx + 1 }}</span>
                    <span class="name">{{ step.label }}</span>
                    <span class="nums">{{ step.count }} 次 · {{ step.sessions }} 会话</span>
                  </div>
                  <div class="bar-track">
                    <div class="bar-fill" :style="{ width: barWidth(step.count) }" />
                  </div>
                </div>
              </div>
            </div>
          </el-col>
          <el-col :span="10">
            <div class="panel">
              <h3>热门款式（事件关联）</h3>
              <el-table :data="topBrands" size="small" v-loading="loading" max-height="360">
                <el-table-column prop="brand_name" label="款式" min-width="140" show-overflow-tooltip />
                <el-table-column prop="count" label="事件数" width="90" />
              </el-table>
              <div v-if="!topBrands.length && !loading" class="muted empty">暂无品牌关联事件</div>
            </div>
          </el-col>
        </el-row>
      </el-tab-pane>

      <el-tab-pane label="访客访问 / 登录" name="logins">
        <div class="panel">
          <h3>
            访客访问分析
            <span class="muted small">
              含未登录浏览 · 北京时间 · {{ loginInfo.geo?.provider || 'GeoIP' }}{{ loginInfo.geo?.offline_xdb ? '（离线 xdb）' : '' }}
            </span>
          </h3>
          <p class="muted small" style="margin: 0 0 12px">
            {{ loginInfo.note || '访客来自转化事件（page_view 等）；真实登录为鉴权成功写入。' }}
          </p>
          <el-row :gutter="8" class="login-stats">
            <el-col :span="6">
              <div class="mini-stat">
                <div class="stat-label">访客会话</div>
                <div class="stat-value sm">{{ loginInfo.visitor_sessions || 0 }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="mini-stat">
                <div class="stat-label">独立 IP</div>
                <div class="stat-value sm">{{ loginInfo.visitor_ips || loginInfo.unique_ips || 0 }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="mini-stat">
                <div class="stat-label">页面浏览</div>
                <div class="stat-value sm">{{ loginInfo.page_views || 0 }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="mini-stat">
                <div class="stat-label">转化事件</div>
                <div class="stat-value sm">{{ loginInfo.visitor_events || 0 }}</div>
              </div>
            </el-col>
          </el-row>
          <el-table
            :data="loginInfo.recent_visitors || []"
            size="small"
            max-height="360"
            v-loading="loginsLoading"
            style="margin-bottom: 20px"
          >
            <el-table-column prop="created_at" label="最近活动(北京)" width="170" />
            <el-table-column prop="label" label="类型" width="90" />
            <el-table-column prop="ip" label="IP" width="130" show-overflow-tooltip />
            <el-table-column prop="region" label="地区/运营商" min-width="150" show-overflow-tooltip>
              <template #default="{ row }">{{ row.region || '-' }}</template>
            </el-table-column>
            <el-table-column prop="event_count" label="事件" width="70" />
            <el-table-column prop="landing_path" label="落地页" min-width="140" show-overflow-tooltip />
            <el-table-column prop="session_id" label="会话" min-width="120" show-overflow-tooltip />
          </el-table>
          <div
            v-if="!(loginInfo.recent_visitors || []).length && !loginsLoading"
            class="muted empty"
          >
            近 {{ days }} 天暂无访客访问记录
          </div>

          <h3 style="margin-top: 8px">
            真实登录
            <span class="muted small">鉴权成功 · user_login_events</span>
          </h3>
          <el-row :gutter="8" class="login-stats">
            <el-col :span="6">
              <div class="mini-stat">
                <div class="stat-label">登录次数</div>
                <div class="stat-value sm">{{ loginInfo.total_logins || 0 }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="mini-stat">
                <div class="stat-label">独立用户</div>
                <div class="stat-value sm">{{ loginInfo.unique_users || 0 }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="mini-stat">
                <div class="stat-label">登录 IP</div>
                <div class="stat-value sm">{{ loginInfo.login_unique_ips || 0 }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="mini-stat">
                <div class="stat-label">登录方式</div>
                <div class="stat-value sm method-brief">{{ methodBrief }}</div>
              </div>
            </el-col>
          </el-row>
          <el-table :data="loginInfo.recent || []" size="small" max-height="320" v-loading="loginsLoading">
            <el-table-column prop="created_at" label="时间(北京)" width="170" />
            <el-table-column prop="label" label="类型" width="90" />
            <el-table-column prop="user_id" label="用户" width="80" />
            <el-table-column prop="method" label="方式" width="90" />
            <el-table-column prop="ip" label="IP" width="130" show-overflow-tooltip />
            <el-table-column prop="region" label="地区/运营商" min-width="160" show-overflow-tooltip>
              <template #default="{ row }">{{ row.region || '-' }}</template>
            </el-table-column>
            <el-table-column prop="landing_path" label="落地页" min-width="140" show-overflow-tooltip />
          </el-table>
          <div v-if="loginInfo.login_available === false" class="muted empty">
            {{ loginInfo.login_message || '暂无登录表' }}
          </div>
          <div v-else-if="!(loginInfo.recent || []).length && !loginsLoading" class="muted empty">
            近 {{ days }} 天暂无真实登录（浏览访客见上方）
          </div>
        </div>
      </el-tab-pane>

      <el-tab-pane label="IP 个人画像" name="ips">
        <div class="panel">
          <div class="events-head">
            <h3>
              IP 个人画像
              <span class="muted small">自有登录/转化 IP · {{ loginInfo.geo?.provider || 'GeoIP' }}</span>
            </h3>
          </div>
          <el-table
            :data="ipProfiles"
            size="small"
            v-loading="ipsLoading"
            max-height="360"
            highlight-current-row
            @row-click="selectIp"
          >
            <el-table-column prop="ip" label="IP" width="130" />
            <el-table-column prop="region" label="地区/运营商" min-width="150" show-overflow-tooltip>
              <template #default="{ row }">{{ row.region || '-' }}</template>
            </el-table-column>
            <el-table-column prop="login_count" label="登录" width="70" />
            <el-table-column prop="event_count" label="事件" width="70" />
            <el-table-column prop="last_at" label="最近(北京)" width="160" />
            <el-table-column label="品牌" min-width="140" show-overflow-tooltip>
              <template #default="{ row }">{{ (row.brands_viewed || []).join('、') || '-' }}</template>
            </el-table-column>
            <el-table-column label="漏斗" min-width="140" show-overflow-tooltip>
              <template #default="{ row }">{{ (row.funnel || []).map(funnelLabel).join(' → ') || '-' }}</template>
            </el-table-column>
          </el-table>
          <div v-if="activeIp" class="session-detail">
            <h4>IP 详情 · {{ activeIp.ip }} · {{ activeIp.region || '地区未知' }}</h4>
            <p class="muted">
              用户 {{ activeIp.user_id || '-' }}
              · 登录 {{ activeIp.login_count || 0 }}
              · 事件 {{ activeIp.event_count || 0 }}
              · 漏斗 {{ (activeIp.funnel || []).map(funnelLabel).join(' → ') || '-' }}
            </p>
            <div class="detail-grid">
              <div>
                <div class="detail-label">登录时间</div>
                <ul class="click-list">
                  <li v-for="(t, i) in (activeIp.login_times || [])" :key="'lt'+i">
                    <span class="c-at">{{ t.at }}</span>
                    <span>{{ t.method || '-' }} {{ t.landing_path || '' }}</span>
                  </li>
                </ul>
                <div v-if="!(activeIp.login_times || []).length" class="muted">暂无登录记录</div>
              </div>
              <div>
                <div class="detail-label">路径 / 品牌</div>
                <ol class="path-list">
                  <li v-for="(p, i) in (activeIp.path_sequence || [])" :key="'ip'+i">{{ p }}</li>
                </ol>
                <p class="muted" style="margin-top:8px">
                  品牌：{{ (activeIp.brands_viewed || []).join('、') || '-' }}
                </p>
              </div>
            </div>
          </div>
          <div v-if="!ipProfiles.length && !ipsLoading" class="muted empty">
            暂无 IP 画像（点击上方表格行可查看详情）
          </div>
        </div>
      </el-tab-pane>

      <el-tab-pane label="会话画像" name="sessions">
        <div class="panel sessions-panel">
          <div class="events-head">
            <h3>会话画像 <span class="muted small">按 session_id 聚合</span></h3>
          </div>
          <el-table
            :data="sessions"
            v-loading="sessionsLoading"
            size="small"
            style="width:100%"
            row-key="session_id"
            @row-click="selectSession"
            highlight-current-row
          >
            <el-table-column prop="last_at" label="最近活动(北京)" width="160" />
            <el-table-column prop="session_id" label="会话" min-width="140" show-overflow-tooltip />
            <el-table-column prop="region" label="地区" width="120" show-overflow-tooltip>
              <template #default="{ row }">{{ row.region || '-' }}</template>
            </el-table-column>
            <el-table-column prop="event_count" label="事件" width="70" />
            <el-table-column prop="dwell_seconds" label="停留(秒)" width="90" />
            <el-table-column label="浏览品牌" min-width="160" show-overflow-tooltip>
              <template #default="{ row }">{{ (row.brands_viewed || []).join('、') || '-' }}</template>
            </el-table-column>
            <el-table-column label="漏斗" min-width="160" show-overflow-tooltip>
              <template #default="{ row }">{{ (row.funnel || []).map(funnelLabel).join(' → ') || '-' }}</template>
            </el-table-column>
          </el-table>
          <div v-if="activeSession" class="session-detail">
            <h4>会话详情 · {{ activeSession.session_id }}</h4>
            <p class="muted">
              IP {{ activeSession.ip || '-' }}
              · {{ activeSession.region || '地区未知' }}
              · {{ activeSession.first_at }} → {{ activeSession.last_at }}
            </p>
            <div class="detail-grid">
              <div>
                <div class="detail-label">路径序列</div>
                <ol class="path-list">
                  <li v-for="(p, i) in (activeSession.path_sequence || [])" :key="i">{{ p }}</li>
                </ol>
                <div v-if="!(activeSession.path_sequence || []).length" class="muted">暂无</div>
              </div>
              <div>
                <div class="detail-label">关键点击 / 画廊交互</div>
                <ul class="click-list">
                  <li v-for="(c, i) in (activeSession.key_clicks || [])" :key="i">
                    <span class="c-at">{{ c.at }}</span>
                    <span>{{ c.label }}</span>
                  </li>
                </ul>
                <div v-if="!(activeSession.key_clicks || []).length" class="muted">暂无</div>
              </div>
            </div>
          </div>
          <div v-if="!sessions.length && !sessionsLoading" class="muted empty">暂无会话数据</div>
        </div>
      </el-tab-pane>

      <el-tab-pane label="事件明细" name="events">
        <div class="panel events-panel">
          <div class="events-head">
            <h3>事件明细 <span class="muted small">北京时间</span></h3>
            <el-select v-model="eventType" clearable placeholder="事件类型" style="width:180px" @change="loadEvents">
              <el-option v-for="opt in eventOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
            </el-select>
          </div>
          <el-table :data="events" v-loading="eventsLoading" size="small" style="width:100%">
            <el-table-column prop="created_at" label="时间(北京)" width="170" />
            <el-table-column prop="label" label="事件" width="120" />
            <el-table-column prop="brand_name" label="款式" min-width="120" show-overflow-tooltip />
            <el-table-column prop="path" label="路径" min-width="160" show-overflow-tooltip />
            <el-table-column label="详情" min-width="140" show-overflow-tooltip>
              <template #default="{ row }">{{ metaBrief(row.meta) }}</template>
            </el-table-column>
            <el-table-column prop="session_id" label="会话" width="140" show-overflow-tooltip />
            <el-table-column prop="ip" label="IP" width="120" />
          </el-table>
          <div class="pager">
            <el-pagination
              background
              layout="total, prev, pager, next"
              :total="eventsTotal"
              :page-size="limit"
              :current-page="page"
              @current-change="(p) => { page = p; loadEvents() }"
            />
          </div>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'

export default defineComponent({
  name: 'ConversionAnalysis',
  setup() {
    const days = ref(7)
    const activeTab = ref('logins')
    const loading = ref(false)
    const eventsLoading = ref(false)
    const sessionsLoading = ref(false)
    const loginsLoading = ref(false)
    const ipsLoading = ref(false)
    const summary = ref({ total_events: 0, funnel: [], by_type: [], top_brands: [] })
    const events = ref([])
    const eventsTotal = ref(0)
    const sessions = ref([])
    const activeSession = ref(null)
    const loginInfo = ref({
      total_logins: 0,
      unique_users: 0,
      unique_ips: 0,
      login_unique_ips: 0,
      visitor_sessions: 0,
      visitor_ips: 0,
      visitor_events: 0,
      page_views: 0,
      recent: [],
      recent_visitors: [],
      geo: {},
      by_method: [],
      note: '',
    })
    const ipProfiles = ref([])
    const activeIp = ref(null)
    const page = ref(1)
    const limit = ref(50)
    const eventType = ref('')
    const highlightKeys = ['page_view', 'click', 'try_on_open']

    const funnel = computed(() => summary.value.funnel || [])
    const topBrands = computed(() => summary.value.top_brands || [])
    const maxCount = computed(() => {
      const nums = (summary.value.funnel || []).map((s) => s.count || 0)
      return Math.max(1, ...nums)
    })
    const methodBrief = computed(() => {
      const list = loginInfo.value.by_method || []
      if (!list.length) return '-'
      return list.slice(0, 3).map((m) => `${m.method || '?'} ${m.count}`).join(' / ')
    })
    const eventOptions = computed(() => {
      const base = (summary.value.funnel || []).map((s) => ({
        value: s.event_type,
        label: s.label
      }))
      const seen = new Set(base.map((b) => b.value))
      ;(summary.value.by_type || []).forEach((s) => {
        if (!seen.has(s.event_type)) {
          base.push({ value: s.event_type, label: s.label || s.event_type })
        }
      })
      return base
    })

    function labelOf(key) {
      const hit = (summary.value.by_type || []).find((x) => x.event_type === key)
      if (hit) return hit.label
      const map = {
        page_view: '页面浏览',
        brand_view: '款式浏览',
        click: '按钮点击',
        try_on_open: '打开试穿'
      }
      return map[key] || key
    }

    function funnelLabel(key) {
      return labelOf(key)
    }

    function countOf(key) {
      const hit = (summary.value.by_type || []).find((x) => x.event_type === key)
      return hit ? hit.count : 0
    }

    function barWidth(count) {
      return Math.max(2, Math.round(((count || 0) / maxCount.value) * 100)) + '%'
    }

    function metaBrief(meta) {
      if (!meta) return '-'
      if (typeof meta === 'string') return meta.slice(0, 60)
      if (typeof meta !== 'object') return String(meta)
      const label = meta.label || meta.element_id || meta.tab || meta.filter || meta.note_id
      if (label) return String(label).slice(0, 60)
      if (meta.dwell_ms != null) return `停留 ${Math.round(meta.dwell_ms / 1000)}s`
      if (meta.seconds != null) return `停留 ${meta.seconds}s`
      try {
        return JSON.stringify(meta).slice(0, 60)
      } catch (e) {
        return '-'
      }
    }

    function selectSession(row) {
      activeSession.value = row
    }

    function selectIp(row) {
      activeIp.value = row
      loadIpDetail(row.ip)
    }

    async function loadSummary() {
      loading.value = true
      try {
        const { data } = await axios.get('/api/conversion/summary', { params: { days: days.value } })
        if (data.code === 200) summary.value = data.data || {}
        else ElMessage.error(data.message || '加载失败')
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '加载转化汇总失败')
      } finally {
        loading.value = false
      }
    }

    async function loadEvents() {
      eventsLoading.value = true
      try {
        const { data } = await axios.get('/api/conversion/events', {
          params: {
            days: days.value,
            event_type: eventType.value || undefined,
            page: page.value,
            limit: limit.value
          }
        })
        if (data.code === 200) {
          events.value = data.data.items || []
          eventsTotal.value = data.data.total || 0
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '加载事件明细失败')
      } finally {
        eventsLoading.value = false
      }
    }

    async function loadSessions() {
      sessionsLoading.value = true
      try {
        const { data } = await axios.get('/api/conversion/sessions', {
          params: { days: days.value, limit: 40 }
        })
        if (data.code === 200) {
          sessions.value = data.data.items || []
          if (activeSession.value) {
            const hit = sessions.value.find((s) => s.session_id === activeSession.value.session_id)
            activeSession.value = hit || null
          }
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '加载会话画像失败')
      } finally {
        sessionsLoading.value = false
      }
    }

    async function loadLogins() {
      loginsLoading.value = true
      try {
        const { data } = await axios.get('/api/conversion/logins', { params: { days: days.value } })
        if (data.code === 200) loginInfo.value = data.data || {}
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '加载登录分析失败')
      } finally {
        loginsLoading.value = false
      }
    }

    async function loadIps() {
      ipsLoading.value = true
      try {
        const { data } = await axios.get('/api/conversion/ips', {
          params: { days: days.value, limit: 40 }
        })
        if (data.code === 200) {
          ipProfiles.value = data.data.items || []
          if (data.data.geo) {
            loginInfo.value = { ...loginInfo.value, geo: data.data.geo }
          }
          if (activeIp.value) {
            const hit = ipProfiles.value.find((x) => x.ip === activeIp.value.ip)
            activeIp.value = hit || null
          }
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '加载 IP 画像失败')
      } finally {
        ipsLoading.value = false
      }
    }

    async function loadIpDetail(ip) {
      if (!ip) return
      try {
        const { data } = await axios.get(`/api/conversion/ips/${encodeURIComponent(ip)}`, {
          params: { days: days.value }
        })
        if (data.code === 200 && data.data) {
          activeIp.value = { ...(activeIp.value || {}), ...data.data }
        }
      } catch (e) {
        /* 列表数据已足够，详情失败不打断 */
      }
    }

    async function reload() {
      page.value = 1
      activeSession.value = null
      activeIp.value = null
      await Promise.all([loadSummary(), loadEvents(), loadSessions(), loadLogins(), loadIps()])
    }

    onMounted(reload)

    return {
      days,
      activeTab,
      loading,
      eventsLoading,
      sessionsLoading,
      loginsLoading,
      ipsLoading,
      summary,
      funnel,
      topBrands,
      events,
      eventsTotal,
      sessions,
      activeSession,
      loginInfo,
      ipProfiles,
      activeIp,
      page,
      limit,
      eventType,
      eventOptions,
      highlightKeys,
      methodBrief,
      labelOf,
      funnelLabel,
      countOf,
      barWidth,
      metaBrief,
      selectSession,
      selectIp,
      reload,
      loadEvents
    }
  }
})
</script>

<style scoped>
.conv-page { padding: 4px 8px 24px; }
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.header h2 { margin: 0; font-size: 20px; color: #1f2d3d; }
.tz-hint, .small { font-size: 12px; font-weight: 400; color: #909399; margin-left: 8px; }
.filters { display: flex; gap: 12px; align-items: center; }
.stats-row { margin-bottom: 16px; }
.conv-tabs { margin-bottom: 8px; }
.stat-card {
  background: #f7f9fc;
  border: 1px solid #e8eef5;
  border-radius: 8px;
  padding: 16px 18px;
  margin-bottom: 12px;
}
.stat-label { color: #8492a6; font-size: 13px; margin-bottom: 6px; }
.stat-value { font-size: 28px; font-weight: 600; color: #1e3c72; }
.stat-value.sm { font-size: 22px; }
.method-brief { font-size: 13px !important; font-weight: 500; line-height: 1.4; word-break: break-all; }
.mini-stat {
  background: #f7f9fc;
  border: 1px solid #e8eef5;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 12px;
}
.login-stats { margin-bottom: 8px; }
.panel {
  background: #fff;
  border: 1px solid #e8eef5;
  border-radius: 8px;
  padding: 16px 18px 12px;
  margin-bottom: 16px;
  min-height: 200px;
}
.panel h3 { margin: 0 0 14px; font-size: 16px; color: #303133; }
.funnel-row { margin-bottom: 12px; }
.funnel-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 13px;
}
.funnel-meta .idx {
  width: 20px; height: 20px; border-radius: 50%;
  background: #1e3c72; color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 11px;
}
.funnel-meta .name { flex: 1; color: #303133; }
.funnel-meta .nums { color: #909399; }
.bar-track {
  height: 10px;
  background: #edf2f7;
  border-radius: 6px;
  overflow: hidden;
}
.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #1e3c72, #2a5298);
  border-radius: 6px;
}
.events-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.events-head h3 { margin: 0; }
.pager { display: flex; justify-content: flex-end; margin-top: 12px; }
.muted { color: #909399; }
.empty { padding: 24px 0; text-align: center; }
.session-detail {
  margin-top: 16px;
  padding: 12px 14px;
  background: #f7f9fc;
  border-radius: 8px;
  border: 1px solid #e8eef5;
}
.session-detail h4 { margin: 0 0 8px; font-size: 14px; }
.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 10px;
}
.detail-label { font-size: 13px; color: #606266; margin-bottom: 6px; font-weight: 600; }
.path-list, .click-list {
  margin: 0;
  padding-left: 18px;
  font-size: 12px;
  color: #303133;
  max-height: 180px;
  overflow: auto;
}
.click-list { list-style: none; padding-left: 0; }
.click-list li { margin-bottom: 4px; }
.c-at { color: #909399; margin-right: 8px; }
@media (max-width: 900px) {
  .detail-grid { grid-template-columns: 1fr; }
}
</style>
